import { publishedBoxApi } from '@/shared/api/sets/publish';
import { useMutation, useQuery, keepPreviousData } from '@tanstack/react-query';

/** 발행함 - 발행 리스트별 조회 */
const usePublishBoxList = (param: API.IntegPubList.Req, enabled: boolean) =>
    useQuery({
        queryKey: ['publish-list', 'pub-list', param],
        queryFn: () => publishedBoxApi.getPublishBoxList(param),
        enabled: enabled,
        placeholderData: keepPreviousData,
    });

/** 발행함 - 발행 닉네임별 조회 */
const usePublishNickList = (param: API.IntegPubNickList.Req, enabled: boolean) =>
    useQuery({
        queryKey: ['publish-list', 'nick-list', param],
        queryFn: () => publishedBoxApi.getPublishNickList(param),
        placeholderData: keepPreviousData,
        enabled: enabled,
    });

/** 발행함 - 발행 학생별 조회 */
const usePublishStntList = (param: API.IntegPubStntList.Req, enabled: boolean) =>
    useQuery({
        queryKey: ['publish-list', 'stnt-list', param],
        queryFn: () => publishedBoxApi.getPublishStntList(param),
        enabled: enabled,
        placeholderData: keepPreviousData,
    });

/** 발행함 - 발행 상세 조회 */
const usePublishDetail = (
    param: API.IntegPubDetail.Req,
    enabled = true,
    refetchOnWindowFocus = true
) =>
    useQuery({
        queryKey: ['publish-detail', 'data-info', param],
        queryFn: () => publishedBoxApi.getPublishDetail(param),
        enabled: enabled,
        refetchOnWindowFocus: refetchOnWindowFocus,
    });

/** 발행함 - 발행 학생별 상세 조회 */
const usePublishStntDetail = (param: API.IntegPubStntDetail.Req) =>
    useQuery({
        queryKey: ['publish-detail', 'stnt-info', param],
        queryFn: () => publishedBoxApi.getPublishStntDetail(param),
    });

/** 발행함 - 발행 상세 조회 - 학생 제출 정보 */
const usePublishSubStnt = (param: API.IntegPubStntSub.Req, enabled = false) =>
    useQuery({
        queryKey: ['publish-detail', 'stnt-sub-info', param],
        queryFn: () => publishedBoxApi.getPublishSubStnt(param),
        enabled: enabled,
    });

/** 발행함 - 발행 옵션 설정 - 최근 발행물 조회 */
const useRecentPublishedList = (param: API.RecentPublishedListType.Req) =>
    useQuery({
        queryKey: ['publish', 'dataInfo', param],
        queryFn: () => publishedBoxApi.getRecentPublishedList(param),
    });

/** 발행함 - 그룹별 발행 리스트 조회 */
const useIntegPubCompleteList = (param: API.IntegPubCompleteList.Req, enabled: boolean) =>
    useQuery({
        queryKey: ['publish', 'complete', param],
        queryFn: () => publishedBoxApi.getIntegPubCompleteList(param),
        enabled: enabled,
    });

/** 발행함 - 클래스 ID 목록 조회 */
const useIntegClaIdList = (param: API.IntegClaIdListType.Req) =>
    useQuery({
        queryKey: ['publish', 'claIdList', param],
        queryFn: () => publishedBoxApi.getClaIdList(param),
    });

/** 발행함 - 클래스 ID 검증 */
const useIntegClaIdVerification = (param: API.IntegPubClaIdVerification.Req) =>
    useQuery({
        queryKey: ['claId', 'claIdList', param],
        queryFn: () => publishedBoxApi.getClaIdVerification(param),
        enabled: false,
    });

/** 발행함 - 학생별 학급 목록 조회 */
const usePubStntClassList = (param: API.IntegPubStntClassList.Req) =>
    useQuery({
        queryKey: ['publish', 'claIdList', param],
        queryFn: () => publishedBoxApi.getPubStntClassList(param),
    });

/** 발행함 - 학생별 상세 리스트 조회 */
const usePubStntDetailList = (param: API.IntegPubSntDetailList.Req, enabled: boolean) =>
    useQuery({
        queryKey: ['publish-list', 'stntListDetail', param],
        queryFn: () => publishedBoxApi.getPubStntDetailList(param),
        enabled: enabled,
        placeholderData: keepPreviousData,
    });

// 발행함 - 닉네임 발행 인증
const usePublishNicknameAuth = () =>
    useMutation({
        mutationKey: ['publish', 'nicknameAuth'],
        mutationFn: (param: API.IntegPubNickNameAuth.Req) =>
            publishedBoxApi.postIntegPubNicknameAuth(param),
    });

// 발행함 - 닉네임 발행 인증 - 비밀번호 변경
const usePublishNewPw = () =>
    useMutation({
        mutationKey: ['publish', 'newPw'],
        mutationFn: (param: API.IntergPubNewPw.Req) => publishedBoxApi.postIntergPubNewPw(param),
    });

// 발행함 - 발행하기
const usePublishExam = () =>
    useMutation({
        mutationKey: ['publish', 'exam'],
        mutationFn: (param: API.IntegPubExam.Req) => publishedBoxApi.postIntegPubExam(param),
    });

// 발행함 - 삭제
const usePublishDelete = () =>
    useMutation({
        mutationKey: ['publish', 'delete'],
        mutationFn: (param: API.IntegPubDelete.Req) => publishedBoxApi.postIntegPubDelete(param),
    });

// 발행함 - 제한 시간 수정
const usePublishEditTimTime = () =>
    useMutation({
        mutationKey: ['publish', 'editTimTime'],
        mutationFn: (param: API.IntegPubEditTimTime.Req) =>
            publishedBoxApi.postIntegEditTimTime(param),
    });

// 발행함  - 발행 상세 - 학생 추가
const usePublishAddStdt = () =>
    useMutation({
        mutationKey: ['publish', 'addStdt'],
        mutationFn: (param: API.IntegPubAddStdt.Req) => publishedBoxApi.postIntegPubAddStdt(param),
    });

// 발행함 - 응시 기간 수정
const usePublishEditPeriod = () =>
    useMutation({
        mutationKey: ['publish', 'editTimTime'],
        mutationFn: (param: API.IntegPubEditPeriod.Req) =>
            publishedBoxApi.postIntegEditPeriod(param),
    });

// 발행함 - 학생 추가
const usePublishAddStnt = () =>
    useMutation({
        mutationKey: ['publish', 'addStnt'],
        mutationFn: (param: API.IntegAddStnt.Req) => publishedBoxApi.postIntegAddStnt(param),
    });

// 발행함 - 힌트 노출 여부 수정
const usePubUpdateHint = () =>
    useMutation({
        mutationKey: ['publish', 'hintUpdate'],
        mutationFn: (param: API.IntegPubUpdateHint.Req) => publishedBoxApi.postPubUpdateHint(param),
    });

// 발행함 - 닉네임 발행 인증 - 비밀번호 초기화
const useIntegPubResetPwd = () =>
    useMutation({
        mutationKey: ['publish', 'editPassword'],
        mutationFn: (param: API.IntergPubResetPwd.Req) =>
            publishedBoxApi.postIntegPubResetPwd(param),
    });

const usePubStdtByNickName = (param: API.IntergPubStdtByNickName.Req) =>
    useQuery({
        queryKey: ['publish', 'stdtByNickName'],
        queryFn: () => publishedBoxApi.getStdtByNickName(param),
    });

export const publishQueries = {
    publish: {
        postPublishExam: usePublishExam,
        postPublishDelete: usePublishDelete,
        postNickNameAuth: usePublishNicknameAuth,
        postSaveNewPw: usePublishNewPw,
        getRecentPublishedList: useRecentPublishedList,
        getIntegPubCompleteList: useIntegPubCompleteList,
        getIntPubStdtByNickName: usePubStdtByNickName,
        getClaIdVerification: useIntegClaIdVerification,
    },
    list: {
        getPublishBoxList: usePublishBoxList,
        getPublishNickList: usePublishNickList,
        getPublishStntList: usePublishStntList,
        getPubStntClassList: usePubStntClassList,
        postIntegPubResetPwd: useIntegPubResetPwd,
    },
    detail: {
        getPublishDetail: usePublishDetail,
        getPublishStntDetail: usePublishStntDetail,
        getPublishSubStnt: usePublishSubStnt,
        getPubStntDetailList: usePubStntDetailList,
        postPublishEditTimTime: usePublishEditTimTime,
        postIntegPubAddStdt: usePublishAddStdt,
        postPublishAddStnt: usePublishAddStnt,
        postPublishEditPeriod: usePublishEditPeriod,
        postPubUpdateHint: usePubUpdateHint,
    },
};
